import React from 'react'

import { useSurveyQueryQuestions } from '~/hooks/useSurveyQuery/useSurveyQueryQuestions'

import { BUILTIN_QUESTION_ROLES } from '../atoms'

import { useBasicOutputWizard } from './useBasicOutputWizard'

export const useMatrixQuestionValidation = (
  matrixRequiredRoles: ReadonlySet<string>,
) => {
  const questions = useSurveyQueryQuestions()
  const { questionAssignments } = useBasicOutputWizard()

  const matrixChildIds = React.useMemo(
    () => new Set(questions.flatMap((q) => q.mt_questions)),
    [questions],
  )

  const isParentMatrixQuestion = React.useCallback(
    (questionId: string): boolean => {
      const q = questions.find((qq) => qq.question_id === questionId)

      return (q?.mt_questions.length ?? 0) > 0
    },
    [questions],
  )

  const isChildMatrixQuestion = React.useCallback(
    (questionId: string): boolean => matrixChildIds.has(questionId),
    [matrixChildIds],
  )

  const isMatrixQuestion = React.useCallback(
    (questionId: string | null): boolean => {
      if (!questionId) {
        return false
      }

      return (
        isParentMatrixQuestion(questionId) || isChildMatrixQuestion(questionId)
      )
    },
    [isParentMatrixQuestion, isChildMatrixQuestion],
  )

  // ロールがマトリクス必須かつ非マトリクス設問が選択されているか
  const getMatrixRequiredError = React.useCallback(
    (role: string, selectedId: string | null): boolean =>
      matrixRequiredRoles.has(role) &&
      selectedId !== null &&
      !isMatrixQuestion(selectedId),
    [matrixRequiredRoles, isMatrixQuestion],
  )

  const hasMatrixError = BUILTIN_QUESTION_ROLES.some((role) => {
    const selectedId = questionAssignments[role]?.[0] ?? null

    return getMatrixRequiredError(role, selectedId)
  })

  return { getMatrixRequiredError, hasMatrixError, isMatrixQuestion }
}
